import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import SiteFooter from '@/components/feature/SiteFooter';
import usePageSchema from '@/hooks/usePageSchema';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const siteUrl = import.meta.env.VITE_SITE_URL || 'https://novaaiwebsites.com';

const pricingSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${siteUrl}/pricing` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/pricing#webpage`,
    url: `${siteUrl}/pricing`,
    name: 'Web Design Pricing Southern California | NOVA AI DIGITAL',
    description: 'Transparent web design pricing for Southern California businesses. Starter from $500, Business from $800, E-Commerce from $1,500. No hidden fees.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${siteUrl}/#website` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'NOVA AI DIGITAL Pricing Plans',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Offer',
          name: 'Starter Website',
          description: 'Custom 1–3 page website, mobile responsive, contact form, basic SEO, Google Business listing. Live in under 24 hours.',
          price: '500',
          priceCurrency: 'USD',
          url: `${siteUrl}/pricing`,
          seller: { '@id': `${siteUrl}/#business` },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Offer',
          name: 'Business Website',
          description: 'Custom 5–7 page website, advanced SEO, AI receptionist setup, lead capture forms, live chat integration, monthly maintenance.',
          price: '800',
          priceCurrency: 'USD',
          url: `${siteUrl}/pricing`,
          seller: { '@id': `${siteUrl}/#business` },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Offer',
          name: 'E-Commerce Website',
          description: 'Custom e-commerce store, up to 50 products, payment gateway, AI automation, email marketing integration, priority support.',
          price: '1500',
          priceCurrency: 'USD',
          url: `${siteUrl}/pricing`,
          seller: { '@id': `${siteUrl}/#business` },
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How fast is "live in under 24 hours"?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most Starter websites go live within 12–24 hours of receiving your content and approval. Business and E-Commerce sites typically take 3–7 days depending on complexity.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I own my website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — 100%. You own the domain, the code, and all assets. We hand everything over to you on completion.',
        },
      },
      {
        '@type': 'Question',
        name: "What's included in monthly maintenance?",
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Monthly maintenance covers hosting support, security updates, minor content changes, and performance monitoring to keep your site running smoothly.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can you work with my existing website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. We can redesign, rebuild, or enhance an existing site. Contact us for a custom assessment and quote.',
        },
      },
    ],
  },
];

const plans = [
  {
    num: '01',
    name: 'Starter',
    price: '$500',
    note: 'One-time',
    tagline: 'Perfect for getting your business online fast.',
    packageId: 'starter',
    features: [
      'Custom 1–3 page website',
      'Mobile responsive design',
      'Contact form',
      'Basic SEO setup',
      'Google Business listing',
      'Live in under 24 hours',
      '1 round of revisions',
    ],
    cta: 'Get Started',
  },
  {
    num: '02',
    name: 'Business',
    price: '$800',
    note: 'One-time + $100/mo',
    tagline: 'Everything you need to grow and convert leads.',
    packageId: 'business',
    features: [
      'Custom 5–7 page website',
      'Mobile responsive design',
      'Lead capture & contact forms',
      'Advanced SEO optimization',
      'Google Business optimization',
      'Live chat integration',
      'AI receptionist setup',
      '3 rounds of revisions',
      'Monthly maintenance',
    ],
    cta: 'Get Started',
    featured: true,
  },
  {
    num: '03',
    name: 'E-Commerce',
    price: '$1,500',
    note: 'One-time + $150/mo',
    tagline: 'Full online store ready to sell from day one.',
    packageId: 'ecommerce',
    features: [
      'Custom e-commerce store',
      'Up to 50 products',
      'Payment gateway setup',
      'Inventory management',
      'Mobile responsive design',
      'Advanced SEO optimization',
      'AI automation setup',
      'Email marketing integration',
      'Unlimited revisions',
      'Priority support',
    ],
    cta: 'Get Started',
  },
];

export default function PricingPage() {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSchema(pricingSchemas, 'pricing');

  const handleCheckout = useCallback(async (packageId: string) => {
    setLoadingPlan(packageId);
    setCheckoutError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId,
          successUrl: `${window.location.origin}/pay/success`,
          cancelUrl: `${window.location.origin}/pricing`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_self');
      } else {
        setCheckoutError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setCheckoutError('Network error. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero heading */}
        <div className="h-20" />
        <div className="px-6 md:px-12 lg:px-16 pt-10 pb-8">
          <h1
            className="text-black leading-none"
            style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(3.5rem, 9vw, 8rem)',
              letterSpacing: '-0.02em',
            }}
          >
            Pricing
          </h1>
        </div>

        {/* Intro */}
        <div className="px-6 md:px-12 lg:px-16 py-10 border-t border-b border-black/8 flex flex-col sm:flex-row items-start justify-between gap-6">
          <p className="text-black/50 text-sm md:text-base leading-relaxed max-w-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            Simple, transparent pricing. No hidden fees, no surprises. Choose the plan
            that fits your business and let&apos;s get to work.
          </p>
          <Link
            to="/contact"
            className="border border-black text-black text-xs uppercase tracking-widest px-8 py-4 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
          >
            Get a Custom Quote
          </Link>
        </div>

        {/* Plans */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20">
          {checkoutError && (
            <div className="mb-6 border border-red-200 bg-red-50 text-red-700 text-sm px-5 py-4 rounded-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              {checkoutError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/8">
            {plans.map((plan, i) => (
              <div
                key={plan.num}
                className={`p-8 md:p-10 border-b md:border-b-0 md:border-r border-black/8 last:border-0 flex flex-col gap-6 ${plan.featured ? 'bg-black text-white' : 'bg-white text-black'}`}
              >
                <div className="flex flex-col gap-2">
                  <span
                    className={`text-xs ${plan.featured ? 'text-white/40' : 'text-black/30'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {plan.num}
                  </span>
                  <h3
                    className={`${plan.featured ? 'text-white' : 'text-black'}`}
                    style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' }}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={`text-sm ${plan.featured ? 'text-white/60' : 'text-black/50'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {plan.tagline}
                  </p>
                </div>

                <div className="flex flex-col gap-0.5">
                  <span
                    className={`${plan.featured ? 'text-white' : 'text-black'}`}
                    style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.02em' }}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-xs ${plan.featured ? 'text-white/40' : 'text-black/40'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {plan.note}
                  </span>
                </div>

                <ul className="flex flex-col gap-3 flex-1">
                  {plan.features.map(f => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm ${plan.featured ? 'text-white/70' : 'text-black/60'}`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <span className={`mt-1.5 w-1 h-1 rounded-full flex-shrink-0 ${plan.featured ? 'bg-white' : 'bg-black'}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handleCheckout(plan.packageId)}
                  disabled={loadingPlan === plan.packageId}
                  className={`border text-xs uppercase tracking-widest px-6 py-3.5 text-center transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${plan.featured ? 'border-white text-white hover:bg-white hover:text-black' : 'border-black text-black hover:bg-black hover:text-white'}`}
                  style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
                >
                  {loadingPlan === plan.packageId ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ row */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20 border-t border-black/8">
          <h2
            className="text-black mb-12"
            style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}
          >
            Common Questions
          </h2>
          <div className="flex flex-col gap-0">
            {[
              { q: 'How fast is "live in under 24 hours"?', a: 'Most Starter websites go live within 12–24 hours of receiving your content and approval. Business and E-Commerce sites typically take 3–7 days depending on complexity.' },
              { q: 'Do I own my website?', a: 'Yes — 100%. You own the domain, the code, and all assets. We hand everything over to you on completion.' },
              { q: 'What\'s included in monthly maintenance?', a: 'Monthly maintenance covers hosting support, security updates, minor content changes, and performance monitoring to keep your site running smoothly.' },
              { q: 'Can you work with my existing website?', a: 'Absolutely. We can redesign, rebuild, or enhance an existing site. Contact us for a custom assessment and quote.' },
            ].map((faq, i) => (
              <div key={i} className="py-7 border-b border-black/8">
                <div className="flex flex-col md:flex-row gap-4 md:gap-20">
                  <p className="text-black md:w-2/5" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: '1rem' }}>{faq.q}</p>
                  <p className="text-black/50 text-sm leading-relaxed md:w-3/5" style={{ fontFamily: 'Inter, sans-serif' }}>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
